import i18n from '../../i18n';
import {
  ContractDocumentData,
  CustomerMasterData,
  Invoice,
} from '../../services/types/contractDetailsResponse';
import {
  ConctractListResponse,
  Contract,
} from '../../services/types/contractListResponse';
import { CustomerMasterDataResponse } from '../../services/types/customerMasterDataResponse';
import {
  InternalContract,
  InternalContractDetail,
  InternalContractDocumentData,
  InternalCustomerMasterData,
  InternalCustomerMasterDataAll,
  InternalInvoice,
} from './types/internalContract';

export const BaseController = {
  mapResponseContractToInternalContract: (
    contract: Contract,
  ): InternalContract => {
    let endDate: string;
    if (contract.openEnd) {
      endDate = i18n.t('open');
    } else if (contract.prolongedEnd) {
      endDate = i18n.t('extended');
    } else {
      endDate = new Date(contract.rcmEndDate).toLocaleDateString(
        i18n.language,
        {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        },
      );
    }

    return {
      contractNr: contract.contractNumber,
      contractPosition: contract.contractPosition,
      contractType: contract.distributionChannel === '86' ? 'RFM' : 'STR', // LTR: 16
      contractSubType: i18n.exists(contract.contractUsage)
        ? i18n.t(contract.contractUsage)
        : '',
      beginDate: contract.delayedStart
        ? i18n.t('delayed')
        : new Date(contract.rcmBeginDate).toLocaleDateString(i18n.language, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }),
      // eslint-disable-next-line object-shorthand
      endDate: endDate,
      hours:
        contract.currentOperatingHours && contract.contractUsageName === 'pbth'
          ? `${contract.currentOperatingHours} h / ${contract.agreedOperatingHours} h`
          : `${contract.agreedOperatingHours} h`,
      rate: `${new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency: String(contract.currency).toUpperCase(),
      }).format(Number(contract?.rate?.replace(/,/g, '.')))} / ${i18n.t(
        contract.periodicity,
      )}`,
      orderNr: contract.orderNumber,
      equipmentSerialNr: contract.equipmentNumber,
      equipmentName: contract.equipmentDescription,
      customerNumber: Number(contract.customerNumber),
      shipToParty: contract.shipToParty,
      billToParty: contract.billToParty,
      contractUsage: i18n.t(contract.contractUsage),
    };
  },

  mapResponseCustomerDataToInternalCustomerData: (
    customerMasterData: CustomerMasterDataResponse,
  ): InternalCustomerMasterData => {
    if (customerMasterData) {
      return {
        text: customerMasterData?.ET_CSTMR_M_D[0]?.NAME1,
        city: customerMasterData?.ET_CSTMR_M_D[0]?.ORT01,
        country: customerMasterData?.ET_CSTMR_M_D[0]?.LAND1,
        id: Number(customerMasterData?.ET_CSTMR_M_D[0]?.KUNNR),
        zipCode: customerMasterData?.ET_CSTMR_M_D[0]?.PSTLZ,
      };
    }
    // fallback if service fails
    return customerMasterData;
  },

  mapResponseToInternalContractList: (
    response: ConctractListResponse,
  ): InternalContract[] => {
    return response.contracts.map((contract: Contract): InternalContract => {
      return BaseController.mapResponseContractToInternalContract(contract);
    });
  },

  mapResponseInvoiceToInternalInvoiceDetail: (
    invoice: Invoice,
  ): InternalInvoice => {
    return {
      billingDate: new Date(invoice.billingDate).toLocaleDateString(
        i18n.language,
        {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        },
      ),
      endDate: new Date(invoice.endDate).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
      billingDateExternal: invoice.billingDate,
      invoiceNumber: `${invoice.invoiceNumber}`,
      invoiceDescription: i18n.exists(invoice.invoiceTypeName)
        ? i18n.t(invoice.invoiceTypeName)
        : '',
      netValue: new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency: String(invoice.currency).toUpperCase(),
      }).format(Number(String(invoice?.netValue)?.replace(/,/g, '.'))),
      startDate: new Date(invoice.startDate).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
    };
  },

  mapResponseContractDocumentDataToInternalContractDocumentData: (
    contractDocumentData: ContractDocumentData,
  ): InternalContractDocumentData => {
    return {
      archiveDocId: contractDocumentData.archiveDocId,
      archiveId: contractDocumentData.archiveId,
      documentDate: new Date(
        contractDocumentData.documentDate,
      ).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
      documentType: contractDocumentData.documentType,
      documentTypeName: contractDocumentData.documentTypeName,
    };
  },

  mapResponseToInternalInvoices: (invoices: Invoice[]): InternalInvoice[] => {
    return invoices.map((invoice: Invoice): InternalInvoice => {
      return BaseController.mapResponseInvoiceToInternalInvoiceDetail(invoice);
    });
  },

  mapResponseToInternalContractDocumentData: (
    contractDocumentsData: ContractDocumentData[],
  ): InternalContractDocumentData[] => {
    return contractDocumentsData
      .sort((a, b) => {
        return a.documentDate > b.documentDate ? -1 : 1;
      })
      .map(
        (
          contractDocumentData: ContractDocumentData,
        ): InternalContractDocumentData => {
          return BaseController.mapResponseContractDocumentDataToInternalContractDocumentData(
            contractDocumentData,
          );
        },
      );
  },

  mapResponseCustomerMasterDataToInternalCustomerMasterData: (
    customerMasterData: CustomerMasterData[],
  ): InternalCustomerMasterDataAll[] => {
    return customerMasterData.map((singleCustormerMasterData) => {
      return {
        type: singleCustormerMasterData.type,
        data: BaseController.mapResponseCustomerDataToInternalCustomerData(
          singleCustormerMasterData.data,
        ),
      };
    });
  },

  mapResponseContractToInternalContractDetail: (
    contract: Contract,
  ): InternalContractDetail => {
    let endDate: string;
    if (contract.openEnd) {
      endDate = i18n.t('open');
    } else if (contract.prolongedEnd) {
      endDate = i18n.t('extended');
    } else {
      endDate = new Date(contract.rcmEndDate).toLocaleDateString(
        i18n.language,
        {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        },
      );
    }
    return {
      contractNr: contract.contractNumber,
      contractPosition: contract.contractPosition,
      contractType: contract.distributionChannel === '86' ? 'RFM' : 'STR', // LTR: 16
      contractSubType: i18n.exists(contract.contractUsage)
        ? i18n.t(contract.contractUsage)
        : '',
      beginDate: contract.delayedStart
        ? i18n.t('delayed')
        : new Date(contract.rcmBeginDate).toLocaleDateString(i18n.language, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }),
      // eslint-disable-next-line object-shorthand
      endDate: endDate,
      hours:
        contract.currentOperatingHours && contract.contractUsageName === 'pbth'
          ? `${Number(contract.currentOperatingHours)} h / ${Number(
              contract.agreedOperatingHours,
            )} h`
          : `${Number(contract.agreedOperatingHours)} h`,
      rate: contract.rate
        ? `${new Intl.NumberFormat(i18n.language, {
            style: 'currency',
            currency: String(contract.currency).toUpperCase(),
          }).format(Number(contract?.rate?.replace(/,/g, '.')))}  / ${i18n.t(
            contract.periodicity,
          )}`
        : null,
      orderNr: contract.orderNumber,
      equipmentSerialNr: contract.equipmentNumber,
      equipmentName: contract.equipmentDescription,
      customerNumber: Number(contract.customerNumber),
      shipToParty: contract.shipToParty,
      billToParty: contract.billToParty,
      contractUsage: contract.contractUsage,
      hourlyRate: contract.hourlyRate
        ? new Intl.NumberFormat(i18n.language, {
            style: 'currency',
            currency: String(contract.currency).toUpperCase(),
          }).format(Number(contract?.hourlyRate?.replace(/,/g, '.')))
        : null,
      contractUsageName: contract.contractUsageName,
      agreedOperatingHours: `${Number(contract.agreedOperatingHours)} h`,
      country: contract.country,
      yearOfConstruction: contract.equipmentConstructionYear,
    };
  },

  mapResponseToInternalContractListDetail: (
    response: ConctractListResponse,
  ): InternalContract[] => {
    return response.contracts.map((contract: Contract): InternalContract => {
      return BaseController.mapResponseContractToInternalContract(contract);
    });
  },
};
